import { BrowserModule } from "@angular/platform-browser";
import { LOCALE_ID, NgModule } from "@angular/core";

import { AppComponent } from "./components/app.component";
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
} from "@ngx-translate/core";
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { RoutingModule } from "./modules/routing.module";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { DecimalPipe, registerLocaleData } from "@angular/common";
import localeSe from "@angular/common/locales/sv";
import { GoogleMapsModule } from "@angular/google-maps";
import { ProductImageFallbackDirective } from "./directives/product-image-fallback.directive";
import { TargetableDirective } from "./directives/targetable.directive";
import {
  IHotjarSettings,
  NGX_HOTJAR_SETTINGS_TOKEN,
  NgxHotjarService,
} from "ngx-hotjar";
import { NgxHotjarRouterModule } from "./modules/patched-hotjar.module";
import { environment } from "./environments/environment";
import { BrandService } from "./services/brand.service";
import { FallbackMissingTranslationHandler } from "./fallbackMissingTranslationsHandler";
import { appPipes } from "./app.module.pipes";
import { appComponents } from "./app.module.components";
import { HotjarService } from "./services/hotjar.service";

registerLocaleData(localeSe);

@NgModule({
  declarations: [
    ProductImageFallbackDirective,
    TargetableDirective,
    ...appPipes,
    ...appComponents,
  ],
  bootstrap: [AppComponent],
  imports: [
    GoogleMapsModule,
    BrowserModule,
    RoutingModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FallbackMissingTranslationHandler,
      },
      defaultLanguage: "se",
    }),
    NgxHotjarRouterModule,
  ],
  providers: [
    ...appPipes,
    DecimalPipe,
    { provide: LOCALE_ID, useValue: "sv-SE" },
    {
      provide: NGX_HOTJAR_SETTINGS_TOKEN,
      useValue: {
        trackingCode: environment.hotjarId,
      } satisfies IHotjarSettings,
    },
    {
      provide: "googleTagManagerId",
      useFactory: (brandService: BrandService) => {
        const brandFromURL = brandService.getBrandFromURL();
        if (brandService.validateBrand(brandFromURL)) {
          return environment.gtmKeys[brandFromURL];
        }
        return environment.gtmKeys.default;
      },
      deps: [BrandService],
    },
    {
      provide: NgxHotjarService,
      useExisting: HotjarService,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
